<template>
  <AppSwitcherTheme />
  <LaunchDarklyContext />

  <div class="layout-authenticated">
    <TheNavigationMenu
      v-model="isNavigationMenuActive"
      class="no-print"
      :aria-label="t('navigationMenu.title')"
    />
    <h1
      v-if="!showAppBar && !mobile && !!pageGroupTitleState.title"
      class="d-sr-only"
    >
      {{ pageGroupTitleState.title }}
    </h1>
    <VAppBar
      v-if="showAppBar || mobile"
      :class="[
        'layout-authenticated__app-bar no-print transition-none',
        !mobile && 'layout-authenticated__app-bar--desktop',
        options?.noAppBarBorder || 'border-b border-border-light border-opacity-100',
      ]"
      color="axle-navbar-top"
      :height="headerHeight"
      flat
    >
      <div
        :class="[
          'layout-authenticated__app-bar-container bg-surface w-100 px-4 pb-md-6 px-md-6 fill-height',
          !mobile && 'layout-authenticated__app-bar-container--rounded mt-4',
          mobile && 'pt-2 pb-2',
        ]"
      >
        <CommonBreadcrumbs
          v-if="!mobile && isBreadcrumbsVisible"
          :items="breadcrumbItems"
        />
        <div
          class="w-100 d-flex align-center align-self-stretch"
        >
          <VBtn
            v-if="mobile"
            class="mr-2"
            icon
            :aria-label="t('navigationMenu.expandMenu')"
            @click="isNavigationMenuActive = !isNavigationMenuActive"
          >
            <VIcon icon="menu" />
          </VBtn>
          <h1
            v-if="!!pageGroupTitleState.title"
            class="text-h2 text-truncate"
            data-testid="elementPageTitle"
          >
            <div class="d-flex align-center">
              <span>
                {{ pageGroupTitleState.title }}
              </span>
              <BaseTag
                v-if="pageGroupTitleState.tagText"
                :label="pageGroupTitleState.tagText"
                :color="pageGroupTitleState.tagColor"
                class="ml-4"
              />
              <div
                v-if="headerRightComponent"
              >
                <component
                  :is="headerRightComponent"
                  v-bind="pageGroupTitleState.headerRightProps"
                />
              </div>
            </div>
            <div
              v-if="subtitleComponent"
              class="subtitle"
            >
              <component
                :is="subtitleComponent"
                v-bind="pageGroupTitleState.subtitleProps"
              />
            </div>
            <div
              v-else-if="pageGroupTitleState.subtitle"
              class="subtitle"
            >
              {{ pageGroupTitleState.subtitle }}
            </div>
          </h1>
          <VSpacer />
          <!--Menu Options-->
          <!--Action Button-->
          <div class="d-flex ga-2">
            <ImpersonationBadge />
            <div class="d-flex ga-2 align-center">
              <!-- Custom components -->
              <template
                v-for="(component, index) in componentContext"
                :key="`custom-${index}`"
              >
                <component
                  :is="component.component"
                  v-if="!component.hide"
                  v-bind="component.props"
                />
              </template>
              <template
                v-for="(button, index) in buttonContext"
                :key="index"
              >
                <component
                  :is="button.variant === 'secondary' ? 'VBtnSecondary' : 'VBtnPrimary'"
                  v-if="!button.isContextMenu && !button.hide"
                  :prepend-icon="button.icon"
                  @click="button.action"
                >
                  {{ button.label }}
                </component>
                <CommonContextMenu
                  v-else-if="!button.hide"
                  :item-groups="button.contextMenuItems && isContextMenuItemGroups(button.contextMenuItems) ? button.contextMenuItems : undefined"
                  :items="button.contextMenuItems && !isContextMenuItemGroups(button.contextMenuItems) ? button.contextMenuItems : undefined"
                  :activator-id="`contextMenuActivator-${index}`"
                >
                  <template #activator>
                    <VBtnTileSecondary
                      :id="`contextMenuActivator-${index}`"
                      color="button.color"
                      variant="flat"
                      :aria-label="t('actions')"
                    >
                      <VIcon icon="more_horiz" />
                    </VBtnTileSecondary>
                  </template>
                </CommonContextMenu>
              </template>
            </div>
          </div>
        </div>
      </div>
    </VAppBar>
    <VMain
      id="main-content"
      tabindex="-1"
      class="layout-authenticated__main transition-none"
    >
      <slot />
    </VMain>
    <!--Create Budget Dialog-->
    <CreateBudgetDialog
      v-if="isBudgetDialogRendered && budgetDialogState.isCreating"
      v-model="budgetDialogState.isOpen"
    />
    <!--Create Budget Group Dialog-->
    <CreateBudgetGroupDialog
      v-if="isCreateBudgetGroupDialogRendered"
      v-model="createBudgetGroupDialogState.isOpen"
    />
    <!--Edit Budget Group Dialog-->
    <EditBudgetGroupDialog
      v-if="isEditBudgetGroupDialogRendered && editBudgetGroupDialogState.budgetGroup"
      v-model="editBudgetGroupDialogState.isOpen"
      :budget-group="editBudgetGroupDialogState.budgetGroup"
    />
    <!--Duplicate Budget Group Dialog-->
    <DuplicateBudgetGroupDialog
      v-if="isDuplicateBudgetGroupDialogRendered && duplicateBudgetGroupDialogState.budgetGroup"
      v-model="duplicateBudgetGroupDialogState.isOpen"
      :budget-group="duplicateBudgetGroupDialogState.budgetGroup"
    />
    <!--Add Budget Group Athlete Panel-->
    <AddBudgetGroupAthletePanel
      v-if="isAddBudgetGroupAthletePanelRendered"
      v-model="addBudgetGroupAthletePanelState.isOpen"
      :budget-group-twrn="addBudgetGroupAthletePanelState.budgetGroupTwrn"
    />
    <!-- Budgets Group Allocations Drawer-->
    <BudgetAllocationsDrawer
      v-if="isBudgetAllocationsDrawerRendered"
      v-model="budgetAllocationsDrawerState.isOpen"
      :budget-group-twrn="budgetAllocationsDrawerState.budgetGroupTwrn"
      @success="budgetAllocationsDrawerState.onSuccess"
    />
    <!--Athlete Profile Drawer-->
    <AthleteProfileDrawer
      v-if="isAthleteProfileDrawerRendered"
      v-model="athleteProfileDrawerState.isOpen"
      :athlete-twrn="athleteProfileDrawerState.athleteTwrn"
      :budget-twrn="athleteProfileDrawerState.budgetTwrn"
      :academic-year-id="athleteProfileDrawerState.academicYearId"
      :default-position-academic-year-id="athleteProfileDrawerState.defaultPositionAcademicYearId"
      :heading-level="athleteProfileDrawerState.headingLevel"
    />

    <ConfirmationDialog />
    <AgreementAthletesDialog
      v-if="isAgreementAthletesDialogRendered"
      v-model="AgreementAthletesDialogState.isOpen"
    />
    <!--View Agreement Dialog-->
    <ViewAgreementDialog />

    <!--Change Position Dialog-->
    <ChangePositionDialog
      v-if="isChangePositionDialogRendered"
      v-model="ChangePositionDialogState.isOpen"
      :athlete="ChangePositionDialogState.athlete"
      :default-academic-year-uuid="ChangePositionDialogState.defaultAcademicYearUuid"
    />

    <!--Change Email Dialog-->
    <ChangeEmailDialog
      v-if="isChangeEmailDialogRendered"
      v-model="ChangeEmailDialogState.isOpen"
      :athlete="ChangeEmailDialogState.athlete"
      :email="ChangeEmailDialogState.email"
    />

    <AddAthleteDocumentDialog
      v-if="isAddAthleteDocumentDialogRendered"
      v-model="addAthleteDocumentDialogState.isOpen"
      :athlete-twrn="addAthleteDocumentDialogState.athleteTwrn"
    />

    <BudgetGroupRecruitDialog
      v-if="isBudgetGroupRecruitDialogRendered"
      v-model="budgetGroupRecruitDialogState.isOpen"
      :budget-group-twrn="budgetGroupRecruitDialogState.budgetGroupTwrn"
    />

    <ChangeAcademicYearDialog
      v-if="isChangeAcademicYearDialogRendered"
      v-model="ChangeAcademicYearDialogState.isOpen"
      :athlete="ChangeAcademicYearDialogState.athlete"
      :current-academic-year-ids="ChangeAcademicYearDialogState.currentAcademicYearIds ?? []"
      :prefill-academic-year-ids="ChangeAcademicYearDialogState.prefillAcademicYearIds ?? []"
    />

    <!--Add Payment Dialog-->
    <AddPaymentDialog
      v-if="isAddPaymentDialogRendered"
      v-model="AddPaymentDialogState.isOpen"
      :agreement-twrn="AddPaymentDialogState.agreementTwrn"
      :athletes="AddPaymentDialogState.athletes ?? undefined"
    />

    <!--Create / Edit Agent Dialog-->
    <CreateAgentDialog
      v-if="isAgentDialogRendered"
      v-model="agentDialogState.isOpen"
      :agent="agentDialogState.agent ?? undefined"
      :associate-athlete-twrn="agentDialogState.associateAthleteTwrn ?? undefined"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref, provide } from 'vue';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import CommonBreadcrumbs from '@/components/common/CommonBreadcrumbs.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import TheNavigationMenu from '@/components/navigation/TheNavigationMenu.vue';
import AppSwitcherTheme from '@/layouts/AppSwitcherTheme.vue';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { usePageGroupTitle } from '@/composables/usePageGroupTitle';
import { useBudgetDialog } from '@/composables/useBudgetDialog';
import { useCreateBudgetGroupDialog } from '@/composables/useCreateBudgetGroupDialog';
import { useEditBudgetGroupDialog } from '@/composables/useEditBudgetGroupDialog';
import { useDuplicateBudgetGroupDialog } from '@/composables/useDuplicateBudgetGroupDialog';
import { useBudgetAllocationsDrawer } from '@/composables/useBudgetAllocationsDrawer';
import { useAthleteProfileDrawer } from '@/composables/useAthleteProfileDrawer';
import { useHeaderButtonContext } from '@/composables/useHeaderButtonContext';
import { isContextMenuItemGroups } from '@/utils/typeHelpers';
import { CommonContextMenu } from '@teamworksdev/vuetify';
import ImpersonationBadge from '@/components/ImpersonationBadge.vue';
import { useAgreementAthletesDialog } from '@/composables/useAgreementAthletesDialog';
import { useHeaderComponentContext } from '@/composables/useHeaderComponentContext';
import { useChangePositionDialog } from '@/composables/useChangePositionDialog';
import { useAddAthleteDocumentDialog } from '@/composables/useAddAthleteDocumentDialog';
import { useRecruitDialog as useBudgetGroupRecruitDialog } from '@/composables/useRecruitDialog';
import { useChangeAcademicYearDialog } from '@/composables/useChangeAcademicYearDialog';
import { useAddPaymentDialog } from '@/composables/useAddPaymentDialog';
import { useChangeEmailDialog } from '@/composables/useChangeEmailDialog';
import { useCreateOrEditAgentDialog } from '@/composables/useCreateOrEditAgentDialog';

const CreateBudgetDialog = defineAsyncComponent(() => import('@/components/budgets/CreateBudgetDialog.vue'));
const CreateBudgetGroupDialog = defineAsyncComponent(() => import('@/components/budgets/CreateBudgetGroupDialog.vue'));
const EditBudgetGroupDialog = defineAsyncComponent(() => import('@/components/budgets/EditBudgetGroupDialog.vue'));
const DuplicateBudgetGroupDialog = defineAsyncComponent(() => import('@/components/budgets/DuplicateBudgetGroupDialog.vue'));
const AthleteProfileDrawer = defineAsyncComponent(() => import('@/components/athletes/AthleteProfileDrawer.vue'));
const AgreementAthletesDialog = defineAsyncComponent(() => import('@/components/agreements/AgreementsBuilder/AgreementAthletesDialog.vue'));
const ViewAgreementDialog = defineAsyncComponent(() => import('@/components/agreements/ViewAgreementPdfDialog.vue'));
const AddBudgetGroupAthletePanel = defineAsyncComponent(() => import('@/components/budgets/AddAthletePanel/AddAthletePanel.vue'));
const ChangePositionDialog = defineAsyncComponent(() => import('@/components/athletes/ChangePositionDialog.vue'));
const AddAthleteDocumentDialog = defineAsyncComponent(() => import('@/components/documents/AddAthleteDocumentDialog.vue'));
const BudgetGroupRecruitDialog = defineAsyncComponent(() => import('@/components/budgets/Recruiting/RecruitDialog.vue'));
const ChangeAcademicYearDialog = defineAsyncComponent(() => import('@/components/athletes/ChangeAcademicYearDialog.vue'));
const ChangeEmailDialog = defineAsyncComponent(() => import('@/components/athletes/ChangeEmailDialog.vue'));
const AddPaymentDialog = defineAsyncComponent(() => import('@/components/agreements/AddPaymentDialog.vue'));
const BudgetAllocationsDrawer = defineAsyncComponent(() => import('@/components/budgets/BudgetAllocationsDrawer.vue'));
const CreateAgentDialog = defineAsyncComponent(() => import('@/components/agents/CreateAgentDialog/CreateAgentDialog.vue'));

import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import LaunchDarklyContext from '@/components/LaunchDarklyContext.vue';
import { useMainStore } from '@/stores/mainStore';
import { useQueryClient } from '@tanstack/vue-query';
import { useGmApi } from '@/composables/useGmApi';
import { getTeamPositionsQueryKey } from '@/utils/queries/teams';
import { datadogLogs } from '@datadog/browser-logs';
import { POSITION_QUERY_STALE_TIME } from '@/composables/usePositionQuery';
import { useAddAthletePanel as useAddBudgetGroupAthletePanel } from '@/composables/useAddAthletePanel';

const props = defineProps<{
  options?: Record<string, unknown>;
}>();

const showAppBar = computed<boolean>(() => {
  if (typeof props.options?.hideAppBar === 'boolean') return !props.options.hideAppBar;
  return true;
});

const { t } = useI18n();
const { mobile } = useDisplay();

const isNavigationMenuActive = ref(false);

// PAGE GROUP TITLE
const { state: pageGroupTitleState, subtitleComponent, headerRightComponent } = usePageGroupTitle();

const {
  breadcrumbItems,
  isBreadcrumbsVisible,
} = useBreadcrumbs();

const headerHeight = computed(() => {
  if (mobile.value) return 64;
  if (props.options?.headerHeight && typeof props.options.headerHeight === 'number') return props.options.headerHeight;
  if (isBreadcrumbsVisible.value) return 135;
  return 87;
});
provide('headerHeight', headerHeight);

// HEADER BUTTONS
const { buttonContext } = useHeaderButtonContext();
const { componentContext } = useHeaderComponentContext();

// CREATE BUDGET DIALOG
const {
  state: budgetDialogState,
  isRendered: isBudgetDialogRendered,
} = useBudgetDialog();

// CREATE BUDGET GROUP DIALOG
const {
  state: createBudgetGroupDialogState,
  isRendered: isCreateBudgetGroupDialogRendered,
} = useCreateBudgetGroupDialog();

// EDIT BUDGET GROUP DIALOG
const {
  state: editBudgetGroupDialogState,
  isRendered: isEditBudgetGroupDialogRendered,
} = useEditBudgetGroupDialog();

// DUPLICATE BUDGET GROUP DIALOG
const {
  state: duplicateBudgetGroupDialogState,
  isRendered: isDuplicateBudgetGroupDialogRendered,
} = useDuplicateBudgetGroupDialog();

// ADD BUDGET GROUP ATHLETE PANEL
const {
  state: addBudgetGroupAthletePanelState,
  isRendered: isAddBudgetGroupAthletePanelRendered,
} = useAddBudgetGroupAthletePanel();

// BUDGET ALLOCATIONS DRAWER
const {
  state: budgetAllocationsDrawerState,
  isRendered: isBudgetAllocationsDrawerRendered,
} = useBudgetAllocationsDrawer();

// ATHLETE PROFILE DRAWER
const {
  state: athleteProfileDrawerState,
  isRendered: isAthleteProfileDrawerRendered,
} = useAthleteProfileDrawer();

// ATHLETES AGREEMENT DIALOG
const {
  state: AgreementAthletesDialogState,
  isRendered: isAgreementAthletesDialogRendered,
} = useAgreementAthletesDialog();

// CHANGE POSITION DIALOG
const {
  state: ChangePositionDialogState,
  isRendered: isChangePositionDialogRendered,
} = useChangePositionDialog();

// CHANGE EMAIL DIALOG
const {
  state: ChangeEmailDialogState,
  isRendered: isChangeEmailDialogRendered,
} = useChangeEmailDialog();

// CHANGE ACADEMIC YEAR DIALOG
const {
  state: ChangeAcademicYearDialogState,
  isRendered: isChangeAcademicYearDialogRendered,
} = useChangeAcademicYearDialog();

// ADD PAYMENT DIALOG
const {
  state: AddPaymentDialogState,
  isRendered: isAddPaymentDialogRendered,
} = useAddPaymentDialog();

const {
  state: addAthleteDocumentDialogState,
  isRendered: isAddAthleteDocumentDialogRendered,
} = useAddAthleteDocumentDialog();

// BUDGET GROUP RECRUIT DIALOG
const {
  state: budgetGroupRecruitDialogState,
  isRendered: isBudgetGroupRecruitDialogRendered,
} = useBudgetGroupRecruitDialog();

// CREATE / EDIT AGENT DIALOG
const {
  state: agentDialogState,
  isRendered: isAgentDialogRendered,
} = useCreateOrEditAgentDialog();

// Prefetch team positions once teamTwrn is known so constructors can map immediately
const mainStore = useMainStore();
const queryClient = useQueryClient();
const { getTeamPositions } = useGmApi();

watch(() => mainStore.teamTwrn, async (twrn) => {
  if (!twrn) return;
  try {
    await queryClient.ensureQueryData({
      queryKey: getTeamPositionsQueryKey(twrn),
      queryFn: () => getTeamPositions(twrn),
      staleTime: POSITION_QUERY_STALE_TIME,
      meta: {
        errorToast: {
          disabled: true,
        },
      },
    });
  } catch (err) {
    if (import.meta.env.MODE !== 'production') console.error('Prefetch positions failed', err);
    datadogLogs.logger.error('Prefetch positions failed', {
      teamTwrn: twrn,
      error: { message: (err as Error).message },
    });
  }
}, { immediate: true });
</script>

<style scoped lang="scss">
.layout-authenticated {
  height: 100%;
  &__app-bar {
    transition: background-color 0.2s;
  }
  &__app-bar-container {
    &--rounded {
      border-top-left-radius: 16px;
    }
  }
  &__main {
    height: 100%;
  }

  &__app-bar--desktop {
    .layout-authenticated__app-bar-container {
        padding-top: 1.6875rem;
    }
  }
}
</style>
